import { type ChainType, chainSettings } from "@/constants/chain-specific/chains";
import { FARM_CHAINS, type FarmChainIds } from "@/constants/project-specific/farms";
import { MASTERCHEFS_CONFIG } from "@/constants/project-specific/farms/masterchef";
import { FARMS_CONFIG } from "@/constants/project-specific/farms/pools";
import type {
  FarmRewardData,
  FarmRewardPrices,
} from "@/constants/project-specific/farms/types";
import type { TokenConfigType } from "@/constants/types/currency";

/**
 * Retrieves the tokens used in single token pools for a given chain ID.
 * @param {ChainId} chainId - The ID of the blockchain chain.
 * @returns {TokenConfigType[]} - An array of token configurations.
 */
function getSingleTokenPoolTokens(chainId: ChainType): TokenConfigType[] {
  const farms = FARMS_CONFIG[chainId as FarmChainIds];
  const singleTokenPools = farms.filter((farm) => !farm.tokenB);
  const singleTokenPoolTokens = singleTokenPools.map((farm) => farm.tokenA);
  return singleTokenPoolTokens;
}
function getUniqueQuoteTokens(chainId: ChainType): TokenConfigType[] {
  const farms = FARMS_CONFIG[chainId as FarmChainIds];
  const quoteTokens = farms.map((farm) => farm.quoteToken);
  const uniqueQuoteTokens = Array.from(new Set(quoteTokens));
  return uniqueQuoteTokens;
}
export async function getFarmRewardPrices() {
  const farmRewardPrices: FarmRewardPrices = {};

  try {
    console.log('🔄 Starting farm reward prices fetch for chains:', FARM_CHAINS);
    
    const fetchPromises = FARM_CHAINS.map(async (chain) => {
    const farmRewardData: FarmRewardData[] = [];
    const masterChefs = MASTERCHEFS_CONFIG[chain as keyof typeof MASTERCHEFS_CONFIG] || [];
    // Loop through each MasterChef contract in config
    if (masterChefs.length < 1) {
      return;
    }

    let contracts: string[] = [];
    const symbols: string[] = [];
    for (const chef of masterChefs) {
      contracts.push(chef.rewardToken.address);
      symbols.push(chef.rewardToken.symbol);
    }
    const quoteTokens = getUniqueQuoteTokens(chain as FarmChainIds);
    const singleTokenPoolTokens = getSingleTokenPoolTokens(chain as FarmChainIds);
    for (const token of singleTokenPoolTokens) {
      if (!symbols.includes(token.symbol)) {
        // duplicate check
        if (!contracts.includes(token.address)) {
          // console.log("Single token pool token: ", token);
          contracts.push(token.address);
        }
      }
    }
    for (const token of quoteTokens) {
      if (!symbols.includes(token.symbol)) {
        contracts.push(token.address);
      }
    }
    // remove duplicates
    contracts = Array.from(new Set(contracts));
    // find quote token prices in farmConfig
    const contractList = contracts.join(",");
    // Get reward token prices for each MasterChef contract
    const base =
      chainSettings[chain as keyof typeof chainSettings].tokenPriceEndpoints
        ?.geckoterminal;
    
    if (!base) {
      console.warn(`⚠️ No token price endpoint configured for chain ${chain}`);
      // Continue with empty reward data for this chain
      farmRewardPrices[chain] = farmRewardData;
      return;
    }
    
    try {
      const endpoint = new URL(contractList, base);
      console.log(`🔄 Fetching prices for chain ${chain}:`, endpoint.toString());
      
      const rewardPrices = await fetch(endpoint, {
        headers: {
          'Accept': 'application/json',
          'User-Agent': 'Mozilla/5.0 (compatible; Farm Price Fetcher/1.0)'
        },
        // Add timeout to prevent hanging requests
        signal: AbortSignal.timeout(10000) // 10 seconds
      });
      
      if (rewardPrices.ok) {
        const rewardData = await rewardPrices.json();
        console.log(`✅ Successfully fetched price data for chain ${chain}`);
        
        // Validate response structure
        if (rewardData?.data?.attributes?.token_prices) {
          // loop through contracts and set price
          for (let i = 0; i < contracts.length; i++) {
            const price = rewardData.data.attributes.token_prices[contracts[i].toLowerCase()];
            farmRewardData.push({
              rewardContract: contracts[i].toLowerCase(),
              rewardPrice: Number(price) || 0, // Default to 0 if price is invalid
            });
          }
        } else {
          console.warn(`⚠️ Invalid response format for chain ${chain}:`, rewardData);
        }
      } else {
        console.error(`❌ Failed to fetch prices for chain ${chain}:`, rewardPrices.status, rewardPrices.statusText);
      }
    } catch (error) {
      console.error(`❌ Error fetching reward prices for chain ${chain}:`, error);
      // Continue with empty reward data for this chain instead of throwing
    }

    farmRewardPrices[chain] = farmRewardData;
  });

    await Promise.all(fetchPromises);
    
    console.log('✅ Farm reward prices fetch completed:', farmRewardPrices);
    return farmRewardPrices;
    
  } catch (error) {
    console.error('❌ Fatal error in getFarmRewardPrices:', error);
    
    // Return empty prices object instead of throwing
    // This allows the app to continue functioning with 0 prices
    const fallbackPrices: FarmRewardPrices = {};
    FARM_CHAINS.forEach(chain => {
      fallbackPrices[chain] = [];
    });
    
    return fallbackPrices;
  }
}
